import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { useAuth, ADMIN_ROLES } from "../lib/auth";
import { api } from "../lib/api";

export const NAV = [
  ["/how-it-works", "How it works", "कैसे काम करता है"],
  ["/categories", "Categories", "श्रेणियाँ"],
  ["/plans", "Plans", "प्लान"],
  ["/rewards", "Rewards", "रिवॉर्ड"],
  ["/referral", "Referral", "रेफरल"],
  ["/security", "Security", "सुरक्षा"],
  ["/faq", "FAQ", "सवाल-जवाब"],
  ["/help", "Help", "सहायता"],
];

const POLICIES = [
  ["terms", "Terms & Conditions", "नियम और शर्तें"],
  ["privacy", "Privacy Policy", "गोपनीयता नीति"],
  ["refund", "Refund & Cancellation", "रिफंड और रद्दीकरण"],
  ["survey-reward-policy", "Survey Reward Policy", "सर्वे रिवॉर्ड नीति"],
  ["referral-policy", "Referral Policy", "रेफरल नीति"],
  ["withdrawal-tax-policy", "Withdrawal & Tax Policy", "निकासी और टैक्स नीति"],
  ["grievance", "Grievance Redressal", "शिकायत निवारण"],
  ["shipping-policy", "Shipping & Delivery", "शिपिंग और डिलीवरी"],
];

function AuthButtons({ mobile }) {
  const { user } = useAuth();
  const { t } = useLang();
  if (user) {
    const to = ADMIN_ROLES.includes(user.role) ? "/admin" : "/app";
    return (
      <Link to={to} data-testid={`nav-dashboard-btn${mobile ? "-m" : ""}`} className="rounded-full bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-2.5 text-sm font-bold text-white shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all hover:shadow-[0_0_25px_rgba(59,130,246,0.7)] hover:scale-[1.02]">
        {t("Dashboard", "डैशबोर्ड")}
      </Link>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <Link to="/login" data-testid={`nav-login-btn${mobile ? "-m" : ""}`} className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-300 hover:text-white transition-colors">
        {t("Login", "लॉगिन")}
      </Link>
      <Link to="/register" data-testid={`nav-register-btn${mobile ? "-m" : ""}`} className="rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] px-6 py-2.5 text-sm font-bold text-[#05070A] shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all hover:shadow-[0_0_25px_rgba(212,175,55,0.5)] hover:scale-[1.02]">
        {t("Join free", "मुफ्त जुड़ें")}
      </Link>
    </div>
  );
}

export default function PublicLayout() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [biz, setBiz] = useState(null);
  useEffect(() => {
    api.get("/public/info").then((r) => setBiz(r.data.business || null)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#05070A] text-slate-200 selection:bg-[#D4AF37]/30 selection:text-white">
      <header className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-[#05070A]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Logo light />
          <nav className="hidden items-center gap-1 xl:flex">
            {NAV.map(([to, en, hi]) => (
              <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className={({ isActive }) => `rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
                {t(en, hi)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-4 xl:flex">
            <LangToggle />
            <AuthButtons />
          </div>
          <div className="flex items-center gap-3 xl:hidden">
            <LangToggle />
            <button onClick={() => setOpen(!open)} className="rounded-lg p-2 text-white bg-white/5" data-testid="mobile-menu-btn" aria-label="Menu">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="absolute top-20 inset-x-0 border-b border-white/10 bg-[#05070A] px-4 py-6 xl:hidden" data-testid="mobile-menu">
            <div className="grid grid-cols-2 gap-2">
              {NAV.map(([to, en, hi]) => (
                <Link key={to} to={to} onClick={() => setOpen(false)} className="rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white">
                  {t(en, hi)}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} className="rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white">{t("Contact", "संपर्क")}</Link>
            </div>
            <div className="mt-6 flex justify-center" onClick={() => setOpen(false)}><AuthButtons mobile /></div>
          </div>
        )}
      </header>
      <main className="pt-20"><Outlet /></main>
      <footer className="mt-20 border-t border-white/5 bg-[#030406] text-slate-400" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-4 lg:px-8">
          <div className="md:col-span-2">
            <Logo light />
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-500">{t("Your opinion builds a better society and better opportunities.", "आपकी राय से बेहतर समाज और बेहतर अवसर।")}</p>
            <p className="mt-8 max-w-lg text-xs leading-relaxed text-slate-600">
              {t(
                "Important: Daily reward limits are maximum limits, not guaranteed earnings. Actual rewards depend on available funded surveys, eligibility, completion and verification. Activation fees are not guaranteed to be recovered.",
                "महत्वपूर्ण: दैनिक रिवॉर्ड सीमाएँ अधिकतम सीमाएँ हैं, गारंटीशुदा कमाई नहीं। वास्तविक रिवॉर्ड उपलब्ध फंडेड सर्वे, पात्रता, समापन और सत्यापन पर निर्भर हैं। एक्टिवेशन शुल्क वापस मिलने की गारंटी नहीं है।"
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Platform", "प्लेटफ़ॉर्म")}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {NAV.slice(0, 6).map(([to, en, hi]) => <li key={to}><Link to={to} className="hover:text-[#D4AF37] transition-colors">{t(en, hi)}</Link></li>)}
              <li><Link to="/contact" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-contact">{t("Contact us", "संपर्क करें")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("Policies", "नीतियाँ")}</p>
            <ul className="mt-6 space-y-3 text-sm">
              {POLICIES.map(([s, en, hi]) => <li key={s}><Link to={`/policy/${s}`} className="hover:text-[#D4AF37] transition-colors" data-testid={`footer-policy-${s}`}>{t(en, hi)}</Link></li>)}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 text-center text-xs text-slate-600" data-testid="footer-legal">
          {biz?.legal_name && (
            <p className="mx-auto max-w-3xl px-4 leading-relaxed" data-testid="footer-business-details">
              {biz.legal_name}{biz.gstin ? ` • GSTIN: ${biz.gstin}` : ""}{biz.address ? ` • ${biz.address}` : ""}
              {biz.support_email ? <><br />{t("Support", "सहायता")}: {biz.support_email}</> : null}
              {biz.support_phone ? <> • {t("Phone", "फ़ोन")}: {biz.support_phone}</> : null}
              {biz.business_hours ? <> ({biz.business_hours})</> : null}
              {biz.grievance_officer ? <>{t(" • Grievance Officer: ", " • शिकायत अधिकारी: ")}{biz.grievance_officer}</> : null}
            </p>
          )}
          <p className="mt-3">© {new Date().getFullYear()} {biz?.legal_name || "Global Enterprises"} (SurveyKaro). {t("All rights reserved.", "सर्वाधिकार सुरक्षित।")}</p>
        </div>
      </footer>
    </div>
  );
}
